import { useState, Suspense } from "react";

import type { ActiveWidget } from "@/pages/EventPage/hooks/useEventWidgets";

import { useGetProfile } from "@/api/bale";
import { Card, CardSubtitleHeader } from "@/components/Card";
import { uuidVersion } from "@/fp/uuidVersion";
import { useCurrentEventSuspense } from "@/pages/EventPage/hooks/useCurrentEvent";

import { EditingNoteCard } from "./EditingNoteCard";
import { StaticNoteCard } from "./StaticNoteCard";

export type NoteWidget = Extract<ActiveWidget, { widget_type: "note" }>;

export function NoteCard({ widget }: { widget: NoteWidget }) {
  return (
    <Suspense
      fallback={
        <Card>
          <CardSubtitleHeader>{widget.title}</CardSubtitleHeader>
        </Card>
      }
    >
      <EditableNoteCardContent widget={widget} />
    </Suspense>
  );
}

function EditableNoteCardContent({ widget }: { widget: NoteWidget }) {
  const me = useGetProfile("@me", { query: { select: (data) => data.data.profile } });
  const event = useCurrentEventSuspense({ query: { select: (data) => data.data } });

  const isTransient = uuidVersion(widget.id) === 4;
  const [isEditing, setIsEditing] = useState(isTransient);
  const isHost = me.isSuccess && event.data.event.account_id === me.data.id;

  if (isEditing) {
    return (
      <EditingNoteCard
        widget={widget}
        onSave={() => setIsEditing(false)}
        onCancel={isTransient ? undefined : () => setIsEditing(false)}
      />
    );
  }

  return <StaticNoteCard widget={widget} onEdit={isHost ? () => setIsEditing(true) : undefined} />;
}
